import React from 'react';
import { t } from '@/lib/i18n';
import { Button } from '@/components/ui';
import { useAuthStore } from '@/stores/authStore';

/**
 * Polish Wave 6 Fix Wave 4 — No Organization Access Screen
 *
 * Shown when a non-super-admin user has zero active organization memberships
 * after bootstrap (e.g. their only membership was deactivated). The user is
 * authenticated — platform login succeeds — but no org context is available.
 *
 * This replaces the generic RorosGuard "Failed to verify onboarding status"
 * error that previously surfaced for these users.
 *
 * Polish Wave 6 Fix Wave 5 — Logout redirect fix
 *
 * The logout button now hard-redirects to /auth after awaiting logout().
 * Previously it called logout() with no navigation; the org-store reset to
 * initialized:false left ProtectedApp spinning forever. A finally-block
 * guarantees the redirect even if the backend logout call fails.
 */

/**
 * Pure orchestration function — extracted for unit-testability.
 * Awaits logout(), then calls redirect() regardless (finally).
 */
export async function performLogout(
  logout: () => Promise<void>,
  redirect: (path: string) => void
): Promise<void> {
  try {
    await logout();
  } catch {
    // Swallow logout errors — we're redirecting regardless.
    // A failed backend logout (network error, 5xx) must not strand the user.
  } finally {
    // Hard redirect guarantees the loader re-runs and lands on /auth,
    // even though the org store was reset to initialized:false.
    redirect('/auth');
  }
}

export function NoOrganizationAccess() {
  const logout = useAuthStore((s) => s.logout);
  const [loggingOut, setLoggingOut] = React.useState(false);

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    await performLogout(logout, (path) => window.location.assign(path));
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-4">
      <h1 className="text-xl font-semibold">
        {t('No organization access')}
      </h1>
      <p className="text-sm text-muted-foreground text-center max-w-sm">
        {t(
          "Your account isn't an active member of any organization. If you think this is a mistake, contact your administrator."
        )}
      </p>
      <Button
        variant="outline"
        onClick={() => void handleLogout()}
        disabled={loggingOut}
        isLoading={loggingOut}
      >
        {t('Log out')}
      </Button>
    </div>
  );
}
